import {Component, computed, effect, inject, model, ModelSignal, Signal} from '@angular/core';
import {IAction, TAnyFormElement, UploadWrapperComponent} from 'frontier/browserkit';
import AppControlGuids from '../../../../api/ControlGuids';
import {
  JSON_EXPORT_ACTION,
  JSON_IMPORT_ACTION,
} from 'frontier/browserkit/src/lib/components/control/table-control/default-form-actions.constant';
import {
  FormElementComponent
} from 'frontier/browserkit/src/lib/components/control/form-control/dynamic-form/form-element/form-element.component';
import {
  CustomDynamicFormComponent
} from 'frontier/browserkit/src/lib/components/control/form-control/dynamic-form/custom-dynamic-form/custom-dynamic-form.component';
import {MatProgressBar} from '@angular/material/progress-bar';
import {MatDivider} from '@angular/material/divider';
import {
  DynamicFormActionComponent
} from 'frontier/browserkit/src/lib/components/control/form-control/dynamic-form/dynamic-form-action/dynamic-form-action.component';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {HomeService} from '../../../home.service';
import {filter, take} from 'rxjs/operators';
import {taxpayersAccountNumberValidator} from './taxpayers-account-number.validator';
import {MatSnackBar} from "@angular/material/snack-bar";

enum EFormKeys {
  shortName = 'tcustomerbaseconfig;0',
  fullName = 'tcustomerbaseconfig;1',
  ik = 'tcustomerbaseconfig;2',
  email = 'tcustomerbaseconfig;3',
  phone = 'tcustomerbaseconfig;4',
  fax = 'tcustomerbaseconfig;5',
  addressName = 'tcustomerbaseconfig;6',
  street = 'tcustomerbaseconfig;7',
  houseNumber = 'tcustomerbaseconfig;8',
  zip = 'tcustomerbaseconfig;9',
  city = 'tcustomerbaseconfig;10',
  bankRecipient = 'tcustomerbaseconfig;11',
  iban = 'tcustomerbaseconfig;12',
  bic = 'tcustomerbaseconfig;13',
  provider = 'tcustomerbaseconfig;14',
  logo = 'tcustomerbaseconfig;15',
  taxpayersAccountNumber = 'tcustomerbaseconfig;16',
  hraEntry = 'tcustomerbaseconfig;17',
  legalStatus = 'tcustomerbaseconfig;18',
}

interface ICustomerBaseFormData {
  shortName: Signal<TAnyFormElement>;
  fullName: Signal<TAnyFormElement>;
  ik: Signal<TAnyFormElement>;
  email: Signal<TAnyFormElement>;
  phone: Signal<TAnyFormElement>;
  fax: Signal<TAnyFormElement>;
  addressName: Signal<TAnyFormElement>;
  street: Signal<TAnyFormElement>;
  houseNumber: Signal<TAnyFormElement>;
  zip: Signal<TAnyFormElement>;
  city: Signal<TAnyFormElement>;
  bankRecipient: Signal<TAnyFormElement>;
  iban: Signal<TAnyFormElement>;
  bic: Signal<TAnyFormElement>;
  provider: Signal<TAnyFormElement>;
  logo: Signal<TAnyFormElement>;
  taxpayersAccountNumber: Signal<TAnyFormElement>;
  hraEntry: Signal<TAnyFormElement>;
  legalStatus: Signal<TAnyFormElement>;
}

@Component({
  selector: 'app-customer-base-data-config-form',
  templateUrl: 'customer-base-data-config-form.component.html',
  styleUrl: 'customer-base-data-config-form.component.scss',
  standalone: true,
  imports: [
    FormElementComponent,
    MatProgressBar,
    MatDivider,
    DynamicFormActionComponent,
    FormsModule,
    ReactiveFormsModule,
    UploadWrapperComponent,
  ],
})
export class CustomerBaseDataConfigFormComponent extends CustomDynamicFormComponent {
  private _homeService: HomeService = inject(HomeService);
  private _snackbar: MatSnackBar = inject(MatSnackBar);

  EFormKeys: typeof EFormKeys = EFormKeys;
  override GUID: string = AppControlGuids.TCustomerBasedataConfiguration;
  override actions: ModelSignal<IAction[]> = model<IAction[]>([
    JSON_EXPORT_ACTION,
    JSON_IMPORT_ACTION,
  ]);

  fb: FormBuilder = inject(FormBuilder);

  constructor() {
    super();
    effect((): void => {
      const form: FormGroup<{ [p: string]: FormControl<any> }> = this.form();
      if (form) {
        form.controls[EFormKeys.taxpayersAccountNumber].addValidators([
          taxpayersAccountNumberValidator,
        ]);
        form.controls[EFormKeys.taxpayersAccountNumber].updateValueAndValidity({emitEvent: false});
      }
    });
  }

  formData: ICustomerBaseFormData = {
    shortName: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.shortName)),
    fullName: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.fullName)),
    ik: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.ik)),
    email: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.email)),
    phone: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.phone)),
    fax: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.fax)),
    addressName: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.addressName)),
    street: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.street)),
    houseNumber: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.houseNumber)),
    zip: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.zip)),
    city: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.city)),
    bankRecipient: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.bankRecipient)),
    iban: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.iban)),
    bic: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.bic)),
    provider: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.provider)),
    logo: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.logo)),
    taxpayersAccountNumber: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.taxpayersAccountNumber)),
    hraEntry: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.hraEntry)),
    legalStatus: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.legalStatus)),
  };

  override saveValue(fc: FormControl, formKey: string | number): void {
    if (this.form()?.invalid) {
      this._snackbar.open(
        'Mindestens ein Formulareintrag hat noch nicht das korrekte Format. Solange können keine Änderungen ' +
        'gespeichert werden.',
        'ok',
        {duration: 10000, verticalPosition: 'top'},
      );
      return;
    }
    super.saveValue(fc, formKey);
  }

  onLogoChanged($event: any): void {
    console.log($event);
    // Set updating
    this.isFormChanging$.next(true);
    this.isFormChanging$.pipe(
      filter((v: boolean): boolean => v === false),
      take(1)
    ).subscribe((): void => {
      // After form is done updating, update the logo in the toolbar
      this._homeService.getCustomLogo();
    });
    this.saveValue(this.form().controls[EFormKeys.logo], EFormKeys.logo);
  }
}
